import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    CircularProgress,
    Snackbar,
    Alert,
    Avatar,
    Chip,
    LinearProgress,
    Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import {
    Add,
    NotificationsActive,
    WorkOutline,
    AssignmentOutlined,
    CancelOutlined,
    WorkHistoryOutlined,
    Groups,
    AutoAwesome,
    Extension,
    CloudDone,
    Sync,
    CreditCard,
} from '@mui/icons-material';
const StatCard = ({ title, value, icon, onClick }) => (
    <Card
        onClick={onClick}
        sx={{
            cursor: 'pointer',
            height: '100%',
            position: 'relative',
            borderBottom: `4px solid #1E3A8A`,
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 10px 20px rgba(30, 58, 138, 0.15)'
            },
            transition: 'all 0.3s ease'
        }}
    >
        <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box
                    sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(30,58,138,0.10)',
                        color: '#1E3A8A',
                    }}
                >
                    {icon}
                </Box>
            </Box>
            <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
                {title}
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                {value}
            </Typography>
        </CardContent>
    </Card>
);

function AdminDashboard() {
    const [jobs, setJobs] = useState([]);
    const [applications, setApplications] = useState([]);
    const [counts, setCounts] = useState({ activeJobs: 0, inactiveJobs: 0, totalJobs: 0, totalApplications: 0,
        pendingReview: 0 });
    const [performanceMetrics, setPerformanceMetrics] = useState({
        newApplicationsToday: 0,
        interviewsScheduled: 0,
        offersSent: 0,
        avgTimeToHire: 0,
        conversionRate: 0,
        insights: []
    });
    const [loading, setLoading] = useState(true);
    const [sendingReminders, setSendingReminders] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { user } = useAuth();
    const navigate = useNavigate();

    // Extension status
    const [extensionStatus, setExtensionStatus] = useState('checking'); // checking, not_installed, installed, connected
    const [extensionVersion, setExtensionVersion] = useState(null);
    const [lastSyncTime, setLastSyncTime] = useState(null);
    const EXTENSION_ID = process.env.REACT_APP_EXTENSION_ID || 'obonigffclmpeofmnhbeodcnlpcglodc'; // Replace with production ID

    useEffect(() => {
        const checkExtension = async () => {
            // If we're not in a Chrome-like environment, it's definitely not installed/accessible this way
            if (typeof window.chrome === 'undefined' || !window.chrome.runtime || !window.chrome.runtime.sendMessage) {
                setExtensionStatus('not_installed');
                return;
            }

            try {
                // Use external messaging to check if extension is present
                window.chrome.runtime.sendMessage(EXTENSION_ID, { type: 'PING' }, (response) => {
                    if (window.chrome.runtime.lastError) {
                        console.log('Extension not detected:', window.chrome.runtime.lastError.message);
                        setExtensionStatus('not_installed');
                    } else if (response && response.ok) {
                        setExtensionStatus('installed');
                        setExtensionVersion(response.version);

                        // Sync with backend
                        api.post('/applications/extension/sync', { version: response.version })
                            .then(() => {
                                setExtensionStatus('connected');
                                setLastSyncTime(new Date().toLocaleString());
                            })
                            .catch(err => console.error('Failed to sync extension status:', err));
                    } else {
                        setExtensionStatus('not_installed');
                    }
                });
            } catch (e) {
                console.error('Error checking extension:', e);
                setExtensionStatus('not_installed');
            }
        };

        if (user) {
            // Delay slightly to ensure browser APIs are ready
            const timer = setTimeout(checkExtension, 1000);
            return () => clearTimeout(timer);
        }
    }, [user, EXTENSION_ID]);
    // Helper to navigate to jobs with a specific tab index
    const goToJobsTab = (tabIndex) => {
        navigate('/admin/jobs', { state: { activeTab: tabIndex } });
    };
    // 2. PASTE THIS NEW FUNCTION RIGHT HERE:
    const goToApplications = (statusFilter) => {
        navigate('/recruiter-applications', { state: { filter: statusFilter, fromDashboard: true } });
    };

    const handleSendReminders = async () => {
        setSendingReminders(true);
        try {
            // Corrected API endpoint
            const response = await api.post('/interviewRoutes/admin/interviews/send-reminders');
            setSnackbar({
                open: true,
                message: `${response.data.message || 'Reminders sent.'}`,
                severity: 'success'
            });
        } catch (error) {
            console.error('Reminder error:', error);
            setSnackbar({
                open: true,
                message: error.response?.data?.message || 'Failed to send reminders.',
                severity: 'error'
            });
        } finally {
            setSendingReminders(false);
        }
    };

    useEffect(() => {
        async function fetchData() {
            try {
                // We only need the jobs call now because our new backend
                // controller provides ALL the counts (Jobs + Apps) in one go.
                const [jobsRes, metricsRes, dashboardRes] = await Promise.all([
                    api.get('/admin/jobs'),
                    api.get('/admin/jobs/analytics/performance-metrics'),
                    api.get('/analytics/admin/jobs/dashboard')
                ]);

                // 1. Extract the jobs array for your list (if needed)
                const jobList = jobsRes.data.jobs || [];

                // 2. Map the pre-calculated counts from your Backend Controller
                // This ensures Blue and Yellow boxes match your Database perfectly
                const serverCounts = jobsRes.data.counts || {
                    activeJobs: 0,
                    inactiveJobs: 0,
                    totalJobs: 0,
                    totalApplications: 0,
                    pendingReview: 0
                };

                setJobs(jobList);
                setCounts(serverCounts); // This updates all 5 dashboard cards at once
                setPerformanceMetrics({
                    ...metricsRes.data,
                    credits: dashboardRes.data.credits
                });
            } catch (error) {
                console.error('Fetch error:', error);
            } finally {
                setLoading(false);
            }
        }

        if (user) fetchData();
    }, [user]);

    // NOTE: The "Purchased Credits" card (Remaining Job/Application/Call/WhatsApp
    // credits) that used to live here has been removed. That same information
    // is now shown on the Subscription page, so it isn't duplicated here.
    // `performanceMetrics.credits` is still fetched above via
    // /analytics/admin/jobs/dashboard since other parts of the app may rely on
    // that same dashboard endpoint response shape — nothing about the fetch
    // itself was touched, only the card that rendered its numbers here.

    if (loading) return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                background: 'linear-gradient(180deg, #f7fbff 0%, #eef4f8 100%)',
            }}
        >
            <CircularProgress />
        </Box>
    );
    return (
        <Box
            sx={{
                minHeight: '100vh',
                py: { xs: 1.5, md: 2.5 },
                px: { xs: 2, md: 4 },
                background: 'linear-gradient(180deg, #f7fbff 0%, #eef4f8 100%)',
            }}
        >
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mb: 3,
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
                p: 2.5,
                borderRadius: '24px',
                background: 'rgba(255,255,255,0.8)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(229, 231, 235, 0.5)',
                boxShadow: '0 4px 20px rgba(10,15,44,0.03)',
            }}
            >
                <Box>
                    <Typography variant="h1" sx={{ color: '#0A0F2C', letterSpacing: '-0.02em' }}>Recruiter Dashboard</Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary', mt: 0.5, fontWeight: 500 }}>
                        Welcome back. Here&apos;s an overview of your recruitment activities and AI insights.
                    </Typography>
                </Box>
                <Box sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                    flexWrap: { xs: 'wrap', md: 'nowrap' },
                    width: '100%',
                    justifyContent: 'flex-start',
                    '& > *': {
                        flexShrink: 1,
                        minWidth: 0
                    }
                }}>
                    {extensionStatus === 'not_installed' && (
                        <Button
                            variant="outlined"
                            color="secondary"
                            startIcon={<Extension />}
                            onClick={() => window.open('https://chrome.google.com/webstore/detail/' + EXTENSION_ID, '_blank')}
                            sx={{
                                borderRadius: '12px',
                                color: '#1E3A8A',
                                borderColor: '#1E3A8A',
                                height: '40px',
                                px: 1,
                                fontSize: '0.8rem',
                                textTransform: 'none',
                                whiteSpace: 'nowrap',
                                '&:hover': { bgcolor: 'rgba(124, 58, 237, 0.05)', borderColor: '#1E3A8A' }
                            }}
                        >
                            Install Chrome Extension
                        </Button>
                    )}
                    {extensionStatus !== 'not_installed' && extensionStatus !== 'checking' && (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 1,
                            py: 0.5,
                            borderRadius: '12px',
                            bgcolor: 'rgba(16, 185, 129, 0.1)',
                            border: '1px solid rgba(16, 185, 129, 0.2)',
                            height: '40px'
                        }}>
                            {extensionStatus === 'connected' ? <CloudDone sx={{ color: '#10B981', fontSize: 18 }} /> : <Sync sx={{ color: '#F59E0B', fontSize: 18 }} />}
                            <Box>
                                <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: extensionStatus === 'connected' ? '#059669' : '#D97706', lineHeight: 1, fontSize: '0.65rem' }}>
                                    Ext {extensionStatus === 'connected' ? 'OK' : 'Sync'}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={sendingReminders ? <CircularProgress size={18} /> : <NotificationsActive sx={{ fontSize: 18 }} />}
                        onClick={handleSendReminders}
                        disabled={sendingReminders}
                        sx={{
                            borderRadius: '12px',
                            border: '1px solid #E5E7EB',
                            color: '#111827',
                            bgcolor: '#fff',
                            height: '40px',
                            px: 1,
                            fontSize: '0.8rem',
                            textTransform: 'none',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {sendingReminders ? '...' : "Send Today's Reminders"}
                    </Button>
                    <Tooltip
                        title={user?.plan === 'free_trial' && (user?.job_limit ?? 1) <= 0
                            ? 'Free Trial job posting limit reached. Please upgrade your plan.'
                            : ''}
                    >
                        <span>
                            <Button
                                variant="contained"
                                startIcon={<Add sx={{ fontSize: 18 }} />}
                                onClick={() => navigate('/admin/jobs/new')}
                                disabled={user?.plan === 'free_trial' && (user?.job_limit ?? 1) <= 0}
                                sx={{
                                    borderRadius: '12px',
                                    bgcolor: '#1E3A8A',
                                    height: '40px',
                                    px: 1,
                                    fontSize: '0.8rem',
                                    textTransform: 'none',
                                    whiteSpace: 'nowrap',
                                    boxShadow: '0 4px 12px rgba(30,58,138,0.2)'
                                }}
                            >
                                Post New Job
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
            </Box>

            <Grid container spacing={3}>
                {/* 1. Active Jobs */}
                <Grid item xs={12} sm={6} md={4} lg={2.4}>
                    <StatCard
                        title="Active Jobs"
                        value={counts.activeJobs}
                        icon={<WorkOutline />}
                        onClick={() => goToJobsTab(1)}
                    />
                </Grid>

                {/* 2. Total Applications */}
                <Grid item xs={12} sm={6} md={4} lg={2.4}>
                    <StatCard
                        title="Applications"
                        value={counts.totalApplications}
                        icon={<Groups />}
                        onClick={() => goToApplications('all')}
                    />
                </Grid>

                {/* 3. Pending Review */}
                <Grid item xs={12} sm={6} md={4} lg={2.4}>
                    <StatCard
                        title="Pending Review"
                        value={counts.pendingReview}
                        icon={<AssignmentOutlined />}
                        onClick={() => goToApplications('pending')}
                    />
                </Grid>

                {/* 4. Inactive Jobs */}
                <Grid item xs={12} sm={6} md={4} lg={2.4}>
                    <StatCard
                        title="Inactive Jobs"
                        value={counts.inactiveJobs}
                        icon={<CancelOutlined />}
                        onClick={() => goToJobsTab(2)}
                    />
                </Grid>

                {/* 5. Total Jobs Posted */}
                <Grid item xs={12} sm={6} md={4} lg={2.4}>
                    <StatCard
                        title="Total Posted"
                        value={counts.totalJobs}
                        icon={<WorkHistoryOutlined />}
                        onClick={() => goToJobsTab(0)}
                    />
                </Grid>
            </Grid>

            <Box sx={{ mt: 4 }}>
                <Card
                    sx={{
                        borderRadius: '24px',
                        overflow: 'hidden',
                        background: 'linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(248,251,255,0.96) 100%)',
                        border: '1px solid rgba(148, 163, 184, 0.18)',
                        boxShadow: '0 14px 40px rgba(15, 23, 42, 0.08)',
                    }}
                >
                    <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                        <Grid container spacing={3} alignItems="stretch">
                            <Grid item xs={12} md={7}>
                                <Box
                                    sx={{
                                        height: '100%',
                                        borderRadius: '22px',
                                        p: { xs: 2, md: 3 },
                                        position: 'relative',
                                        overflow: 'hidden',
                                        background: 'linear-gradient(135deg, rgba(239,246,255,0.9) 0%, rgba(255,255,255,0.98) 100%)',
                                    }}
                                >
                                    <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', mb: 1 }}>
                                        Welcome back, Recruiter!
                                    </Typography>

                                    <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 620, lineHeight: 1.8 }}>
                                        Stay on top of your job postings, review applications faster, and make smarter hiring decisions every day.
                                    </Typography>

                                    <Box
                                        sx={{
                                            mt: 3,
                                            p: 3,
                                            borderRadius: '24px',
                                            background: 'rgba(255, 255, 255, 0.4)',
                                            backdropFilter: 'blur(8px)',
                                            border: '1px solid rgba(255, 255, 255, 0.5)',
                                            boxShadow: '0 8px 32px rgba(31, 38, 135, 0.07)',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <AutoAwesome sx={{ color: '#1E3A8A', fontSize: 20 }} />
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E3A8A', textTransform: 'uppercase', letterSpacing: 1 }}>
                                                AI Insights
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                            {performanceMetrics.insights.length > 0 ? (
                                                performanceMetrics.insights.map((insight, i) => (
                                                    <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                                                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#1E3A8A', mt: 1 }} />
                                                        <Typography variant="body2" sx={{ color: '#334155', fontWeight: 500 }}>
                                                            {insight}
                                                        </Typography>
                                                    </Box>
                                                ))
                                            ) : (
                                                [
                                                    "No real-time insights available yet.",
                                                    "Complete more interviews to see AI trends.",
                                                    "Post more jobs to increase application volume."
                                                ].map((insight, i) => (
                                                    <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                                                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#1E3A8A', mt: 1 }} />
                                                        <Typography variant="body2" sx={{ color: '#334155', fontWeight: 500 }}>
                                                            {insight}
                                                        </Typography>
                                                    </Box>
                                                ))
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={5}>
                                <Box
                                    sx={{
                                        height: '100%',
                                        borderRadius: '22px',
                                        p: { xs: 2, md: 3 },
                                        background: 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(239,246,255,0.92) 100%)',
                                        border: '1px solid rgba(148, 163, 184, 0.16)',
                                        position: 'relative',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', mb: 3 }}>
                                        Performance Metrics
                                    </Typography>

                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                        {[
                                            { label: 'New Applications Today', value: performanceMetrics.newApplicationsToday, total: Math.max(performanceMetrics.newApplicationsToday, 20), color: '#1E3A8A' },
                                            { label: 'Interviews Scheduled', value: performanceMetrics.interviewsScheduled, total: Math.max(performanceMetrics.interviewsScheduled, 10), color: '#1E3A8A' },
                                            { label: 'Offers Sent (This Month)', value: performanceMetrics.offersSent, total: Math.max(performanceMetrics.offersSent, 5), color: '#F59E0B' },
                                            { label: 'Avg Time To Hire', value: `${performanceMetrics.avgTimeToHire} Days`, sub: 'Industry avg: 22 days', color: '#3B82F6' },
                                            { label: 'Conversion Rate', value: `${performanceMetrics.conversionRate}%`, progress: performanceMetrics.conversionRate, color: '#EC4899' }
                                        ].map((metric, i) => (
                                            <Box key={i}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748b' }}>{metric.label}</Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>{metric.value}</Typography>
                                                </Box>
                                                {metric.total ? (
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={(metric.value / metric.total) * 100}
                                                        sx={{ height: 6, borderRadius: 3, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: metric.color } }}
                                                    />
                                                ) : metric.progress ? (
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={metric.progress}
                                                        sx={{ height: 6, borderRadius: 3, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: metric.color } }}
                                                    />
                                                ) : (
                                                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>{metric.sub}</Typography>
                                                )}
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>

                        <Box sx={{ mt: 5 }}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>Quick Actions</Typography>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                        <Tooltip title={user?.plan === 'free_trial' && (user?.job_limit ?? 1) <= 0 ? 'Free Trial job posting limit reached. Please upgrade your plan.' : ''}>
                                            <span>
                                                <Button variant="contained" onClick={() => navigate('/admin/jobs/new')} disabled={user?.plan === 'free_trial' && (user?.job_limit ?? 1) <= 0} sx={{ py: 1.5, borderRadius: '12px', width: '100%' }}>Post New Job</Button>
                                            </span>
                                        </Tooltip>
                                        <Button variant="outlined" onClick={() => goToApplications('all')} sx={{ py: 1.5, borderRadius: '12px' }}>All Applications</Button>
                                        <Button variant="outlined" onClick={() => goToApplications('pending')} sx={{ py: 1.5, borderRadius: '12px' }}>Pending Reviews</Button>
                                        <Button variant="outlined" onClick={() => navigate('/analytics')} sx={{ py: 1.5, borderRadius: '12px' }}>View Analytics</Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    </CardContent>
                </Card>
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
}

export default AdminDashboard;